import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { 
    Typography, 
    Box, 
    IconButton, 
    Divider, 
    Button,
} from "@mui/material";
import {
  Done as DoneIcon,
  CancelOutlined as RemoveIcon,
  Dehaze as DehazeIcon,
} from "@mui/icons-material";
// utilities
import { 
  setModules,
} from "../../../../features/roleStepper/roleStepperSlice";
import { formatEnum } from "../../../../utils/string";

// components

const UnselectedPermissionItem = ({ item, index, provided , displayField }) => {

  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid grey",
        borderRadius: "10px",
        padding: "2px 12px",
        margin: 0.5,
        height: 30,
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      <Typography variant="body2">{item[displayField] || formatEnum(item.name)}</Typography>
    </Box>
  );
};

const SelectedPermissionItem = ({ item, index, provided, onRemove,displayField }) => {
  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "#eee",
        borderRadius: "10px",
        padding: "2px 12px",
        margin: 0.5,
        height: 30,
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      <Box display="flex" alignItems="center">
        <DoneIcon sx={{ marginRight: 1, fontSize: 16 }} />
        <Typography variant="body2">{item[displayField] || formatEnum(item.name)}</Typography>
      </Box>

      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        sx={{ padding: 0.5, ml: 1 }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const PermissionContainer = ({
  title,
  droppableId,
  items,
  isSelected = false,
  onRemove = null,
  placeholderText = "",
}) => {
  return (
    <Box flex={1}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      <Droppable droppableId={droppableId} direction="horizontal">
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              minHeight: 200,
              maxHeight: 300,
              border: "1px solid grey",
              padding: 1,
              borderRadius: 1,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexWrap: "wrap",
              alignContent: "flex-start",
            }}
          >
            {/* Render placeholder text only if no items and not being dragged over */}
            {items.length === 0 && !snapshot.isDraggingOver && (
              <Typography
                variant="body2"
                sx={{
                  color: "grey",
                  margin: 0.5,
                  width: "100%",
                }}
              >
                {placeholderText}
              </Typography>
            )}

            {/* Render items */}
            {items.map((item, index) => (
              <Draggable
                key={item.id.toString()}
                draggableId={item.id.toString()}
                index={index}
              >
                {(provided) => {
                  return isSelected ? (
                    <SelectedPermissionItem
                      item={item}
                      index={index}
                      provided={provided}
                      onRemove={onRemove}
                      displayField='display_name'
                    />
                  ) : (
                    <UnselectedPermissionItem
                      item={item}
                      index={index}
                      provided={provided}
                      displayField='display_name'

                    />
                  );
                }}
              </Draggable>
            ))}

            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Box>
  );
};

// Function to return the placeholder text based on the selection status and title
const getPlaceholderText = (isSelected, title) => {
  return isSelected
    ? `Drag permissions from the '${title}' to assign them to this role.`
    : "No Permissions are available";
};

// Function to return the divider with the icon in the middle
const VerticalDivider = () => {
  return (
    <Box sx={{ position: "relative", marginX: 1 }}>
      <Divider orientation="vertical" flexItem />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          padding: 0.5,
        }}
      >
        <DehazeIcon fontSize="small" />
      </Box>
    </Box>
  );
};

const PermissionModule = ({
  moduleName,
  moduleId,
  permissions,
  updatePermissions,
}) => {
  const { left, right } = permissions;

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      const items =
        source.droppableId === `left-${moduleId}` ? [...left] : [...right];
      const setItems =
        source.droppableId === `left-${moduleId}` ? "left" : "right";

      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);

      updatePermissions(moduleId, { ...permissions, [setItems]: items });
    } else {
      if (source.droppableId === `left-${moduleId}`) {
        const updatedLeft = [...left];
        const [moved] = updatedLeft.splice(source.index, 1);
        updatePermissions(moduleId, {
          left: updatedLeft,
          right: [...right, moved],
        });
      } else {
        const updatedRight = [...right];
        const [moved] = updatedRight.splice(source.index, 1);
        updatePermissions(moduleId, {
          left: [...left, moved],
          right: updatedRight,
        });
      }
    }
  };

  const handleRemoveFromRight = (index) => {
    const itemToRemove = right[index];
    updatePermissions(moduleId, {
      left: [...left, itemToRemove],
      right: right.filter((_, i) => i !== index),
    });
  };

  return (
    <Box sx={{ marginBottom: 4 }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box display="flex" gap={2}>
          {/* Left Section - Available Permissions */}
          <PermissionContainer
            title={moduleName} // Pass module name as title for left container
            droppableId={`left-${moduleId}`}
            items={left}
            isSelected={false}
            placeholderText={getPlaceholderText(false, moduleName)}
          />

          {/* Divider */}
          <VerticalDivider />

          {/* Right Section - Selected Permissions */}
          <PermissionContainer
            title="Selected Permissions"
            droppableId={`right-${moduleId}`}
            items={right}
            isSelected={true}
            onRemove={handleRemoveFromRight}
            placeholderText={getPlaceholderText(true, moduleName)}
          />
        </Box>
      </DragDropContext>
    </Box>
  );
};

const RolePermissionSelector = () => {
    const dispatch = useDispatch();
    const modules = useSelector((state) => state.roleStepper.modules);

  // Function to update permissions in Redux state
  const updatePermissions = (moduleId, updatedPermissions) => {
    dispatch(
      setModules({
        ...modules,
        [moduleId]: {
          ...updatedPermissions,
          moduleName: modules[moduleId]?.moduleName, // Preserve module name
        },
      })
    );
  };

//   const logSelectedPermissions = () => {
//     const selectedPermissions = Object.values(modules).flatMap(
//       (mod) => mod.right
//     );
//     console.log("Selected Permissions:", modules);
//   };

  return (
    <Box sx={{ padding: "20px" }}>
      {/* <Button
        variant="contained"
        color="primary"
        onClick={logSelectedPermissions}
        sx={{ marginTop: 2 }}
      >
        Log Selected Permissions
      </Button> */}
      {Object.entries(modules).map(([moduleId, permissions]) => (
        <PermissionModule
          key={moduleId}
          moduleId={moduleId}
          moduleName={permissions.moduleName} // Use actual module name from data
          permissions={{ left: permissions.left, right: permissions.right }}
          updatePermissions={updatePermissions}
        />
      ))}
    </Box>
  );
};

export default RolePermissionSelector;
